import { Fragment, useEffect, useState } from 'react';
import { breakpoints, Button, Dialog, Inline } from '@etg/wings';
import { css, etiColors } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { Overlay } from '@eti/components';
import isFunction from 'lodash/isFunction';
import { FORM_SECTION } from '../../utils/constants';
import { getBundleName, getNewActiveBundle } from '../../utils/utils';
import ProductReadMoreContent from '../bundle-overlay/ProductReadMoreContent';
import BundleModalContent from './BundleModalContent';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const generateOptionKey = index => `option_${index}`;
const buttonStyles = css`
  width: 100%;
`;
const tabWrapperStyles = css`
  @media (min-width: ${breakpoints._768}) {
    margin-inline-end: -34px;
  }

  margin-inline-end: -24px;
`;
const dialogWrapperStyles = css`
  h2:first-child {
    display: flex;
    flex-direction: column;
  }
`;
const tabStyles = isActive => css`
  background-color: ${etiColors.white};
  border: 0;
  border-bottom: 3px solid ${isActive ? etiColors.info.focus : etiColors.grayScale[6]};
  color: ${isActive ? etiColors.black : etiColors.grayScale[8]};
  cursor: pointer;
  flex: 1;
  font-weight: 700;
  outline: none;
  padding: 10px;
  white-space: nowrap;
`;
const characteristicsListStyles = css`
  color: ${etiColors.grayScale[10]};
  list-style-type: disc;
  margin: 10px;

  ul {
    line-height: 2.25;
    list-style-type: circle;
    margin: 0 12px;
  }
`;
const characteristicStyles = css`
  font-size: 0.75rem;
  margin-bottom: 10px;
  margin-inline-start: 7px;

  h4 {
    color: ${etiColors.grayScale[10]};
    font-size: 0.825rem;
    font-weight: 700;
  }

  strong {
    font-size: 0.825rem;
  }
`;
const buttonWrapperStyles = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
`;
const BundleModal = _ref => {
  var _activeBundle$feature;
  let {
    isTDOrStepPage,
    noExtrasText,
    onChange,
    onClose,
    options,
    selectedBundleToOpen,
    setSelectedBundleToOpen,
    shouldShow,
    titles
  } = _ref;
  const {
    t
  } = useTranslation();
  const [activeBundle, setActiveBundle] = useState(() => getNewActiveBundle(options, selectedBundleToOpen));
  const handleChange = () => {
    if (isFunction(onChange)) {
      onChange({
        value: activeBundle.id
      }, `${FORM_SECTION}.value`);
      const radioButton = document.getElementById(`choice.ancillaryBundle.${activeBundle.id}`);
      if (radioButton) {
        radioButton.focus();
      }
    }
    if (!isTDOrStepPage) {
      const selectedBundleElement = Array.from(document.getElementsByName('value')).find(element => {
        return element.value === activeBundle.id;
      });
      selectedBundleElement === null || selectedBundleElement === void 0 || selectedBundleElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    setSelectedBundleToOpen(null);
  };
  useEffect(() => {
    if (selectedBundleToOpen) {
      setActiveBundle(getNewActiveBundle(options, selectedBundleToOpen));
    }
  }, [options, selectedBundleToOpen]);
  return isTDOrStepPage ? /*#__PURE__*/_jsxs(Overlay.Drawer, {
    dataTestId: "modal-overlay-drawer",
    handleClose: onClose,
    id: "product-bundle-info-drawer",
    isOpen: shouldShow,
    children: [/*#__PURE__*/_jsx(Overlay.Header, {
      onClose: onClose,
      title: activeBundle === null || activeBundle === void 0 ? void 0 : activeBundle.title
    }), /*#__PURE__*/_jsx(Overlay.Content, {
      "data-testid": "modal-overlay-drawer-content",
      children: activeBundle === null || activeBundle === void 0 || (_activeBundle$feature = activeBundle.features) === null || _activeBundle$feature === void 0 ? void 0 : _activeBundle$feature.map((item, index) => /*#__PURE__*/_jsx(BundleModalContent, {
        bundleDescription: item.description,
        bundleName: item.name,
        bundleReadMoreText: item.readMoreText,
        dimensions: null,
        id: index,
        piecesAndWeight: null,
        termsUrl: null
      }, generateOptionKey(`product-bundle_tab_feature_${index}`)))
    }), /*#__PURE__*/_jsx(Overlay.Footer, {
      children: /*#__PURE__*/_jsx("div", {
        className: buttonWrapperStyles,
        children: /*#__PURE__*/_jsx(Button, {
          "aria-describedby": "modal-title",
          "data-testid": `${selectedBundleToOpen}-readMoreDrawer-selectButton`,
          onClick: handleChange,
          variant: "primary",
          children: t('Product.Bundle.Select.Button.Text')
        })
      })
    })]
  }) : /*#__PURE__*/_jsxs(Dialog, {
    className: dialogWrapperStyles,
    closeButtonAriaLabel: t('ScreenReader.CloseButtonDialog.AriaLabel.Text'),
    footer: activeBundle && /*#__PURE__*/_jsx(Button, {
      className: buttonStyles,
      "data-testid": `${getBundleName(activeBundle)}-readMoreModal-selectButton`,
      onClick: handleChange,
      variant: "primary",
      children: t('Product.Bundle.Select.Button.Text')
    }),
    isOpen: shouldShow,
    onDismiss: onClose,
    title: t('Product.FlexibleTicket.Bundle.Title'),
    children: [/*#__PURE__*/_jsx(Inline, {
      className: tabWrapperStyles,
      spacing: 0,
      children: options === null || options === void 0 ? void 0 : options.map((item, index) => {
        const packageName = getBundleName(item);
        const isActive = packageName === selectedBundleToOpen;
        return /*#__PURE__*/_jsx("button", {
          className: tabStyles(isActive),
          "data-testid": `bundleModal-tab-${packageName}`,
          onClick: () => setSelectedBundleToOpen(packageName),
          type: "button",
          children: titles[index]
        }, generateOptionKey(`product-bundle_tab_${index}`));
      })
    }), options === null || options === void 0 ? void 0 : options.map((item, index) => {
      const bundleName = getBundleName(item);
      const isActive = bundleName === selectedBundleToOpen;
      const {
        features
      } = item;
      return isActive && /*#__PURE__*/_jsx(Fragment, {
        children: /*#__PURE__*/_jsx("ul", {
          className: characteristicsListStyles,
          children: features.length > 0 ? features.map((feature, key) => {
            return feature.isIncluded && /*#__PURE__*/_jsx("li", {
              className: characteristicStyles,
              children: /*#__PURE__*/_jsx(ProductReadMoreContent, {
                dataTestId: `bundleModal-tab-${bundleName}-${feature.name}`,
                description: feature.description,
                name: feature.name,
                readMoreText: feature.readMoreText,
                termsUrl: feature.termsUrl
              })
            }, generateOptionKey(`feature_${key}`));
          }) : /*#__PURE__*/_jsx("li", {
            className: characteristicStyles,
            children: noExtrasText
          })
        })
      }, generateOptionKey(`product-bundle_${index}`));
    })]
  });
};
export default BundleModal;
//# sourceMappingURL=BundleModal.js.map